import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ArtifactModal from './components/ArtifactModal'
import EntryModal from './components/EntryModal'
import {
  CalendarIcon,
  ChartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  DownloadIcon,
  EditIcon,
  FileIcon,
  InfoIcon,
  LockIcon,
  LogOutIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  UsersIcon,
} from './components/Icons'
import Login from './components/Login'
import MonthSelectorModal from './components/MonthSelectorModal'
import PasswordModal from './components/PasswordModal'
import ResourceModal from './components/ResourceModal'
import TimesheetGrid from './components/TimesheetGrid'
import { createDemoService } from './lib/demoData'
import {
  formatHours,
  formatMonth,
  getMonthDays,
  getMonthRange,
  isCurrentMonth,
  monthStartKey,
  shiftMonth,
  startOfMonth,
} from './lib/date'
import { isSupabaseConfigured, supabase } from './lib/supabase'
import { createSupabaseService, getMembership } from './lib/supabaseService'

const VIEW_META = {
  timesheets: {
    eyebrow: 'Monthly timesheets',
    label: 'Timesheets',
  },
  resources: {
    eyebrow: 'Team workspace',
    label: 'Resource management',
  },
  evidence: {
    eyebrow: 'Weekly evidence',
    label: 'Evidence',
  },
  reports: {
    eyebrow: 'Monthly overview',
    label: 'Management report',
  },
}

function FullScreenLoading({ label = 'Loading timesheets…' }) {
  return (
    <main className="full-screen-state">
      <span className="loading-spinner" aria-hidden="true" />
      <strong>{label}</strong>
    </main>
  )
}

function AccessDenied({ email, onSignOut }) {
  return (
    <main className="full-screen-state full-screen-state--card">
      <span className="state-icon state-icon--warning"><InfoIcon size={28} /></span>
      <h1>Access not approved</h1>
      <p><strong>{email}</strong> is not authorised to use this timesheet.</p>
      <button className="button button--secondary" onClick={onSignOut} type="button">
        <LogOutIcon size={17} />
        Sign out
      </button>
    </main>
  )
}

function SetupError({ error, onRetry, onSignOut }) {
  return (
    <main className="full-screen-state full-screen-state--card">
      <span className="state-icon state-icon--warning"><InfoIcon size={28} /></span>
      <h1>Unable to open TimeGrid</h1>
      <p>The session was found, but the workspace access check did not complete.</p>
      <pre className="error-detail">{error}</pre>
      <div className="state-actions">
        <button className="button button--primary" onClick={onRetry} type="button">
          <RefreshIcon size={17} />
          Retry
        </button>
        <button className="button button--secondary" onClick={onSignOut} type="button">
          Sign out
        </button>
      </div>
    </main>
  )
}

function initials(name = '') {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function avatarHue(name = '') {
  return [...name].reduce((total, character) => total + character.charCodeAt(0), 0) % 360
}

function csvCell(value) {
  const text = String(value ?? '')
  const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text
  return `"${safeText.replace(/"/g, '""')}"`
}

function makeCsv(rows) {
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n')
}

function artifactMonthValues(artifact) {
  const values = Array.isArray(artifact?.month_starts) && artifact.month_starts.length
    ? artifact.month_starts
    : [artifact?.month_start]
  return [...new Set(values.filter(Boolean))].sort()
}

function formatMonthStartLabel(value) {
  if (!value) return ''
  const [year, month] = value.slice(0, 7).split('-').map(Number)
  if (!year || !month) return value
  return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(
    new Date(year, month - 1, 1),
  )
}

function formatArtifactDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

function parseMonthStart(value) {
  const [year, month] = String(value || '').slice(0, 7).split('-').map(Number)
  return year && month ? new Date(year, month - 1, 1) : startOfMonth(new Date())
}

function formatProfileDate(value) {
  if (!value) return '—'
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day))
}

function buildResourceStats(resourceList, entryList, artifactList) {
  const stats = new Map(
    resourceList.map((resource) => [
      resource.id,
      {
        ...resource,
        workedHours: 0,
        leaveDays: 0,
        sickDays: 0,
        holidayDays: 0,
        artifactCount: 0,
        artifactItems: [],
      },
    ]),
  )

  entryList.forEach((entry) => {
    const resource = stats.get(entry.resource_id)
    if (!resource) return
    if (entry.status === 'worked') resource.workedHours += Number(entry.hours || 0)
    if (entry.status === 'leave') resource.leaveDays += 1
    if (entry.status === 'sick') resource.sickDays += 1
    if (entry.status === 'holiday') resource.holidayDays += 1
  })

  artifactList.forEach((artifact) => {
    const resource = stats.get(artifact.resource_id)
    if (!resource) return
    resource.artifactCount += 1
    resource.artifactItems.push(artifact)
  })

  return resourceList.map((resource) => stats.get(resource.id))
}

function downloadCsv(rows, fileName) {
  const blob = new Blob(['\uFEFF', makeCsv(rows)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function EmptySection({ filtered, onAddResource, onClearSearch }) {
  return (
    <div className="empty-state empty-state--panel">
      <span className="empty-state-icon">
        {filtered ? <SearchIcon size={24} /> : <PlusIcon size={24} />}
      </span>
      <h3>{filtered ? 'No matching resources' : 'No resources yet'}</h3>
      <p>
        {filtered
          ? 'Try another resource name.'
          : 'Add a resource to start tracking the month.'}
      </p>
      {filtered ? (
        <button className="button button--secondary" onClick={onClearSearch} type="button">
          Clear search
        </button>
      ) : (
        <button className="button button--primary" onClick={onAddResource} type="button">
          <PlusIcon size={17} />
          Add resource
        </button>
      )}
    </div>
  )
}

function ResourceManagementView({
  rows,
  filtered,
  selectedMonths,
  onAddResource,
  onChooseMonths,
  onClearSearch,
  onEditResource,
  onOpenEvidence,
  onOpenResume,
  onOpenTimesheet,
}) {
  return (
    <section className="section-panel">
      <div className="section-panel-header resource-management-header">
        <div>
          <h2>Resource management</h2>
          <p>Edit resource profiles and compare monthly totals and weekly-file status.</p>
        </div>
        <button className="button button--secondary" onClick={onChooseMonths} type="button">
          <CalendarIcon size={16} />
          {selectedMonths.length} month{selectedMonths.length === 1 ? '' : 's'} selected
        </button>
      </div>

      <div className="resource-month-strip" aria-label="Selected reporting months">
        <strong>Showing</strong>
        {selectedMonths.map((value) => <span key={value}>{formatMonthStartLabel(value)}</span>)}
      </div>

      {rows.length ? (
        <div className="directory-table-wrap">
          <table className="directory-table directory-table--resources directory-table--resource-profiles">
            <thead>
              <tr>
                <th scope="col">Resource</th>
                <th scope="col">Vendor</th>
                <th scope="col">Start date</th>
                <th scope="col">Contact</th>
                <th scope="col">Month</th>
                <th scope="col">Monthly hours</th>
                <th scope="col">Weekly files</th>
                <th scope="col">Resume</th>
                <th scope="col">Comments</th>
                <th className="directory-actions-head" scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((resource) => (
                <tr key={`${resource.id}:${resource.reportingMonthStart}`}>
                  <td>
                    <div className="directory-resource">
                      <span
                        className="resource-avatar"
                        style={{ '--avatar-hue': avatarHue(resource.name) }}
                      >
                        {initials(resource.name)}
                      </span>
                      <span>
                        <strong>{resource.name}</strong>
                        <small>{resource.role || 'Resource'}</small>
                      </span>
                    </div>
                  </td>
                  <td className="directory-muted directory-profile-value">{resource.vendor_name || '—'}</td>
                  <td className="directory-muted directory-profile-value">{formatProfileDate(resource.start_date)}</td>
                  <td className="directory-muted directory-profile-value">{resource.email || '—'}</td>
                  <td className="directory-month">{resource.reportingMonthLabel}</td>
                  <td><strong>{formatHours(resource.workedHours)}h</strong></td>
                  <td>
                    <span className={`status-pill ${resource.artifactCount ? 'status-pill--complete' : 'status-pill--missing'}`}>
                      {resource.artifactCount
                        ? `${resource.artifactCount} file${resource.artifactCount === 1 ? '' : 's'}`
                        : 'Missing'}
                    </span>
                  </td>
                  <td>
                    {resource.resume_storage_path ? (
                      <button
                        className="table-link-button"
                        onClick={() => onOpenResume(resource)}
                        title={resource.resume_label || 'Open resume'}
                        type="button"
                      >
                        <FileIcon size={14} />
                        <span>Open resume</span>
                      </button>
                    ) : (
                      <span className="directory-muted">Not uploaded</span>
                    )}
                  </td>
                  <td>
                    <span className="directory-comment" title={resource.comments || ''}>
                      {resource.comments || '—'}
                    </span>
                  </td>
                  <td>
                    <div className="directory-actions">
                      <button
                        className="button button--secondary button--compact"
                        onClick={() => onOpenTimesheet(resource, resource.reportingMonthStart)}
                        type="button"
                      >
                        Timesheet
                      </button>
                      <button
                        className="button button--secondary button--compact"
                        onClick={() => onOpenEvidence(resource, resource.reportingMonthStart)}
                        type="button"
                      >
                        Evidence
                      </button>
                      <button
                        className="button button--secondary button--compact"
                        onClick={() => onEditResource(resource)}
                        type="button"
                      >
                        <EditIcon size={14} />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptySection
          filtered={filtered}
          onAddResource={onAddResource}
          onClearSearch={onClearSearch}
        />
      )}
    </section>
  )
}

function EvidenceView({
  resources,
  filtered,
  monthLabel,
  onAddResource,
  onClearSearch,
  onManageEvidence,
}) {
  return (
    <section className="section-panel">
      <div className="section-panel-header">
        <div>
          <h2>Weekly files for {monthLabel}</h2>
          <p>Upload each resource’s weekly Excel files and review them by reporting month.</p>
        </div>
      </div>

      {resources.length ? (
        <div className="evidence-list">
          {resources.map((resource) => (
            <article className="evidence-row evidence-row--files" key={resource.id}>
              <div className="directory-resource evidence-resource">
                <span
                  className="resource-avatar"
                  style={{ '--avatar-hue': avatarHue(resource.name) }}
                >
                  {initials(resource.name)}
                </span>
                <span>
                  <strong>{resource.name}</strong>
                  <small>{resource.role || resource.email || 'Resource'}</small>
                </span>
              </div>

              <div className="evidence-summary evidence-summary--files">
                <span className={`status-pill ${resource.artifactCount ? 'status-pill--complete' : 'status-pill--missing'}`}>
                  {resource.artifactCount
                    ? `${resource.artifactCount} weekly file${resource.artifactCount === 1 ? '' : 's'}`
                    : 'No weekly files'}
                </span>

                {resource.artifactCount ? (
                  <div className="evidence-file-preview">
                    {resource.artifactItems.map((artifact) => {
                      const monthCount = artifactMonthValues(artifact).length
                      return (
                        <button
                          className="evidence-file-preview-item"
                          key={artifact.id}
                          onClick={() => onManageEvidence(resource)}
                          title={`Open files for ${resource.name}`}
                          type="button"
                        >
                          <FileIcon size={15} />
                          <span>
                            <strong>{artifact.label}</strong>
                            <small>
                              {artifact.created_at ? formatArtifactDate(artifact.created_at) : 'Uploaded file'}
                              {monthCount > 1 ? ` · shown in ${monthCount} months` : ''}
                            </small>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <small>No weekly spreadsheet has been uploaded for this month.</small>
                )}
              </div>

              <button
                className="button button--secondary"
                onClick={() => onManageEvidence(resource)}
                type="button"
              >
                <FileIcon size={16} />
                {resource.artifactCount ? 'Manage files' : 'Upload weekly files'}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <EmptySection
          filtered={filtered}
          onAddResource={onAddResource}
          onClearSearch={onClearSearch}
        />
      )}
    </section>
  )
}

function ManagementReportView({ resources, filtered, monthLabel, onAddResource, onClearSearch }) {
  return (
    <section className="section-panel">
      <div className="section-panel-header">
        <div>
          <h2>Management report</h2>
          <p>Resource totals and weekly-file status for {monthLabel}.</p>
        </div>
      </div>

      {resources.length ? (
        <div className="directory-table-wrap">
          <table className="directory-table report-table">
            <thead>
              <tr>
                <th scope="col">Resource</th>
                <th scope="col">Worked hours</th>
                <th scope="col">Leave</th>
                <th scope="col">Sick</th>
                <th scope="col">Holiday</th>
                <th scope="col">Weekly files</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((resource) => {
                const isComplete = resource.workedHours > 0 && resource.artifactCount > 0
                return (
                  <tr key={resource.id}>
                    <td>
                      <div className="directory-resource">
                        <span
                          className="resource-avatar"
                          style={{ '--avatar-hue': avatarHue(resource.name) }}
                        >
                          {initials(resource.name)}
                        </span>
                        <span>
                          <strong>{resource.name}</strong>
                          <small>{resource.role || 'Resource'}</small>
                        </span>
                      </div>
                    </td>
                    <td><strong>{formatHours(resource.workedHours)}h</strong></td>
                    <td>{resource.leaveDays}</td>
                    <td>{resource.sickDays}</td>
                    <td>{resource.holidayDays}</td>
                    <td>{resource.artifactCount}</td>
                    <td>
                      <span className={`status-pill ${isComplete ? 'status-pill--complete' : 'status-pill--missing'}`}>
                        {isComplete ? 'Complete' : resource.artifactCount ? 'Hours pending' : 'Files missing'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptySection
          filtered={filtered}
          onAddResource={onAddResource}
          onClearSearch={onClearSearch}
        />
      )}
    </section>
  )
}

export default function App() {
  const demoMode = !isSupabaseConfigured
  const demoServiceRef = useRef(null)
  const authUserIdRef = useRef(demoMode ? 'demo' : null)
  if (demoMode && !demoServiceRef.current) demoServiceRef.current = createDemoService()

  const [authReady, setAuthReady] = useState(demoMode)
  const [session, setSession] = useState(null)
  const [membership, setMembership] = useState(demoMode ? { display_name: 'Demo user', is_admin: true } : null)
  const [membershipLoading, setMembershipLoading] = useState(!demoMode)
  const [setupError, setSetupError] = useState('')
  const [membershipAttempt, setMembershipAttempt] = useState(0)

  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [resources, setResources] = useState([])
  const [entries, setEntries] = useState([])
  const [artifacts, setArtifacts] = useState([])
  const [search, setSearch] = useState('')
  const [activeView, setActiveView] = useState('timesheets')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dataError, setDataError] = useState('')
  const [reloadToken, setReloadToken] = useState(0)

  const [selectedEntry, setSelectedEntry] = useState(null)
  const [selectedArtifactsResource, setSelectedArtifactsResource] = useState(null)
  const [resourceModalOpen, setResourceModalOpen] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [monthSelectorOpen, setMonthSelectorOpen] = useState(false)
  const [resourceMonths, setResourceMonths] = useState(() => [monthStartKey(new Date())])
  const [resourceMonthData, setResourceMonthData] = useState([])
  const [resourceMonthsLoading, setResourceMonthsLoading] = useState(false)
  const [resourceMonthsError, setResourceMonthsError] = useState('')
  const [resourceMonthsReloadToken, setResourceMonthsReloadToken] = useState(0)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [toast, setToast] = useState(null)

  const days = useMemo(() => getMonthDays(month), [month])
  const monthStart = useMemo(() => monthStartKey(month), [month])
  const monthRange = useMemo(() => getMonthRange(month), [month])
  const monthLabel = useMemo(() => formatMonth(month), [month])

  const service = useMemo(() => {
    if (demoMode) return demoServiceRef.current
    if (!session?.user?.id) return null
    return createSupabaseService(supabase, { id: session.user.id })
  }, [demoMode, session?.user?.id])

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() })
  }, [])

  useEffect(() => {
    if (!toast) return undefined
    const timer = window.setTimeout(() => setToast(null), 3500)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (demoMode) return undefined
    let active = true

    function applySession(nextSession) {
      if (!active) return

      const nextUserId = nextSession?.user?.id || null
      const userChanged = authUserIdRef.current !== nextUserId
      authUserIdRef.current = nextUserId

      setSession(nextSession)
      setAuthReady(true)

      if (!nextUserId) {
        setMembership(null)
        setMembershipLoading(false)
        setSetupError('')
      } else if (userChanged) {
        setMembership(null)
        setMembershipLoading(true)
        setSetupError('')
      }
    }

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!active) return
        if (error) {
          setSetupError(error.message)
          setMembershipLoading(false)
          setAuthReady(true)
          return
        }
        applySession(data.session || null)
      })
      .catch((error) => {
        if (!active) return
        setSetupError(error.message || String(error))
        setMembershipLoading(false)
        setAuthReady(true)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession)

      if (
        event === 'SIGNED_IN' &&
        (window.location.hash.includes('access_token=') || window.location.search.includes('code='))
      ) {
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [demoMode])

  useEffect(() => {
    if (demoMode || !session?.user?.email) return undefined
    let active = true
    let timeoutId

    setMembershipLoading(true)
    setSetupError('')

    const timeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error('The access check timed out. Check your connection and try again.'))
      }, 12000)
    })

    Promise.race([getMembership(supabase, session.user.email), timeout])
      .then((member) => {
        if (active) setMembership(member)
      })
      .catch((error) => {
        if (active) {
          setMembership(null)
          setSetupError(error.message || String(error))
        }
      })
      .finally(() => {
        if (timeoutId) window.clearTimeout(timeoutId)
        if (active) setMembershipLoading(false)
      })

    return () => {
      active = false
      if (timeoutId) window.clearTimeout(timeoutId)
    }
  }, [demoMode, session?.user?.id, session?.user?.email, membershipAttempt])

  const loadMonth = useCallback(async () => {
    if (!service || (!demoMode && !membership)) return

    setLoading(true)
    setDataError('')
    try {
      const data = await service.loadMonth(monthStart, monthRange)
      setResources(data.resources)
      setEntries(data.entries)
      setArtifacts(data.artifacts)
    } catch (error) {
      setDataError(error.message || 'Timesheet data could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [service, demoMode, membership, monthStart, monthRange])

  useEffect(() => {
    loadMonth()
  }, [loadMonth, reloadToken])

  useEffect(() => {
    setResourceMonths([monthStart])
  }, [monthStart])

  useEffect(() => {
    if (activeView !== 'resources' || !service || (!demoMode && !membership)) return undefined
    let active = true

    setResourceMonthsLoading(true)
    setResourceMonthsError('')

    Promise.all(
      resourceMonths.map(async (reportingMonthStart) => {
        const reportingMonth = parseMonthStart(reportingMonthStart)
        const data = await service.loadMonth(reportingMonthStart, getMonthRange(reportingMonth))
        return {
          ...data,
          monthStart: reportingMonthStart,
          monthLabel: formatMonth(reportingMonth),
        }
      }),
    )
      .then((data) => {
        if (active) setResourceMonthData(data)
      })
      .catch((error) => {
        if (active) {
          setResourceMonthData([])
          setResourceMonthsError(error.message || 'The selected months could not be loaded.')
        }
      })
      .finally(() => {
        if (active) setResourceMonthsLoading(false)
      })

    return () => {
      active = false
    }
  }, [activeView, service, demoMode, membership, resourceMonths, resourceMonthsReloadToken])


  const entryMap = useMemo(() => {
    const map = new Map()
    entries.forEach((entry) => map.set(`${entry.resource_id}:${entry.work_date}`, entry))
    return map
  }, [entries])

  const filteredResources = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return resources
    return resources.filter((resource) => resource.name?.toLowerCase().includes(query))
  }, [resources, search])

  const resourceStats = useMemo(
    () => buildResourceStats(resources, entries, artifacts),
    [resources, entries, artifacts],
  )

  const resourceManagementRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    const rows = resourceMonthData.flatMap((monthData) =>
      buildResourceStats(monthData.resources, monthData.entries, monthData.artifacts)
        .filter((resource) => !query || resource.name?.toLowerCase().includes(query))
        .map((resource) => ({
          ...resource,
          reportingMonthStart: monthData.monthStart,
          reportingMonthLabel: monthData.monthLabel,
        })),
    )

    return rows.sort((left, right) => {
      const order = (left.sort_order ?? 0) - (right.sort_order ?? 0)
      if (order) return order
      const nameOrder = left.name.localeCompare(right.name)
      if (nameOrder) return nameOrder
      return left.reportingMonthStart.localeCompare(right.reportingMonthStart)
    })
  }, [resourceMonthData, search])

  const filteredResourceStats = useMemo(() => {
    const visibleIds = new Set(filteredResources.map((resource) => resource.id))
    return resourceStats.filter((resource) => visibleIds.has(resource.id))
  }, [filteredResources, resourceStats])

  const summary = useMemo(() => {
    const workedHours = entries.reduce(
      (total, entry) => total + (entry.status === 'worked' ? Number(entry.hours || 0) : 0),
      0,
    )
    const leaveDays = entries.filter((entry) => entry.status === 'leave').length
    const sickDays = entries.filter((entry) => entry.status === 'sick').length
    const holidayDays = entries.filter((entry) => entry.status === 'holiday').length
    const evidenceResources = new Set(artifacts.map((artifact) => artifact.resource_id)).size

    return {
      workedHours,
      leaveDays,
      sickDays,
      holidayDays,
      evidenceResources,
      missingEvidence: Math.max(resources.length - evidenceResources, 0),
    }
  }, [entries, artifacts, resources.length])

  async function signInWithPassword(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function updatePassword(password) {
    setSaving(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setPasswordModalOpen(false)
      showToast('Password updated. Future sign-ins will not require an email link.')
    } finally {
      setSaving(false)
    }
  }

  async function signOut() {
    if (!demoMode) await supabase.auth.signOut()
  }

  async function saveEntry(payload) {
    setSaving(true)
    try {
      const saved = await service.saveEntry(payload)
      setEntries((current) => {
        const withoutExisting = current.filter(
          (entry) =>
            !(
              entry.resource_id === payload.resource_id && entry.work_date === payload.work_date
            ),
        )
        return saved ? [...withoutExisting, saved] : withoutExisting
      })
      setSelectedEntry(null)
      showToast(saved ? 'Day updated.' : 'Day cleared.')
      setResourceMonthsReloadToken((value) => value + 1)
      return saved
    } finally {
      setSaving(false)
    }
  }

  function openAddResource() {
    setEditingResource(null)
    setResourceModalOpen(true)
  }

  function openEditResource(resource) {
    setEditingResource(resource)
    setResourceModalOpen(true)
  }

  function closeResourceModal() {
    setResourceModalOpen(false)
    setEditingResource(null)
  }

  async function saveResource(payload) {
    setSaving(true)
    try {
      if (editingResource) {
        const updated = await service.updateResource(editingResource, payload)
        setResources((current) =>
          current
            .map((resource) => (resource.id === updated.id ? updated : resource))
            .sort(
              (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name),
            ),
        )
        setSelectedArtifactsResource((current) =>
          current?.id === updated.id ? { ...current, ...updated } : current,
        )
        setSearch((current) => {
          const query = current.trim().toLowerCase()
          return !query || updated.name.toLowerCase().includes(query) ? current : ''
        })
        setResourceMonthsReloadToken((value) => value + 1)
        closeResourceModal()
        showToast(`${updated.name} updated.`)
        return updated
      }

      const resource = await service.addResource(payload)
      setResources((current) =>
        [...current, resource].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name),
        ),
      )
      setSearch('')
      setResourceMonthsReloadToken((value) => value + 1)
      closeResourceModal()
      showToast(`${resource.name} added.`)
      return resource
    } finally {
      setSaving(false)
    }
  }

  async function openResourceResume(resource) {
    const newWindow = window.open('about:blank', '_blank')
    if (newWindow) newWindow.opener = null

    try {
      const url = await service.getResourceResumeUrl(resource)
      if (!url) {
        if (newWindow) newWindow.close()
        showToast(
          demoMode
            ? 'Demo mode stores the resume name only. Connect Supabase to open the uploaded file.'
            : 'This resource does not have an uploaded resume.',
          'error',
        )
        return
      }
      if (newWindow) newWindow.location.href = url
      else window.location.assign(url)
    } catch (error) {
      if (newWindow) newWindow.close()
      showToast(error.message || 'The resume could not be opened.', 'error')
    }
  }

  async function uploadArtifacts({ files, monthStarts }) {
    const uploaded = []

    for (const file of files) {
      const artifact = await service.uploadArtifact({
        resourceId: selectedArtifactsResource.id,
        monthStart,
        monthStarts,
        file,
      })
      uploaded.push(artifact)

      if (artifactMonthValues(artifact).includes(monthStart)) {
        setArtifacts((current) => [artifact, ...current])
      }
    }

    setResourceMonthsReloadToken((value) => value + 1)
    showToast(
      `${uploaded.length} weekly file${uploaded.length === 1 ? '' : 's'} uploaded for ${monthStarts.length} month${monthStarts.length === 1 ? '' : 's'}.`,
    )
    return uploaded
  }

  async function deleteArtifact(artifact) {
    await service.deleteArtifact(artifact)
    setArtifacts((current) => current.filter((item) => item.id !== artifact.id))
    setResourceMonthsReloadToken((value) => value + 1)
    showToast('Evidence removed.')
  }

  async function openArtifact(artifact) {
    return service.getArtifactUrl(artifact)
  }

  async function resetDemo() {
    const confirmed = window.confirm('Reset all browser demo data to the original sample?')
    if (!confirmed) return
    demoServiceRef.current.reset()
    setReloadToken((value) => value + 1)
    setResourceMonthsReloadToken((value) => value + 1)
    showToast('Demo data reset.')
  }

  function openResourceTimesheet(resource, reportingMonthStart = monthStart) {
    setMonth(parseMonthStart(reportingMonthStart))
    setSearch(resource.name)
    setActiveView('timesheets')
  }

  function openResourceEvidence(resource, reportingMonthStart = monthStart) {
    setSelectedArtifactsResource(null)
    setMonth(parseMonthStart(reportingMonthStart))
    setSearch(resource.name)
    setActiveView('evidence')
  }

  function exportCurrentViewCsv() {
    const visibleRows = activeView === 'resources' ? resourceManagementRows : filteredResourceStats
    if (!visibleRows.length) {
      showToast('No resources are available to export.', 'error')
      return
    }

    const monthSlug = monthStart.slice(0, 7)
    let rows
    let fileName
    let exportedCount = visibleRows.length

    if (activeView === 'timesheets') {
      rows = [
        [
          'Month',
          'Resource',
          'Role',
          'Contact',
          ...days.map((day) => `${day.number} ${day.weekday}`),
          'Total hours',
          'Weekly files',
        ],
        ...filteredResourceStats.map((resource) => [
          monthLabel,
          resource.name,
          resource.role || '',
          resource.email || '',
          ...days.map((day) => {
            const entry = entryMap.get(`${resource.id}:${day.key}`)
            if (!entry) return ''
            if (entry.status === 'worked') return formatHours(entry.hours)
            if (entry.status === 'leave') return 'L'
            if (entry.status === 'sick') return 'SL'
            if (entry.status === 'holiday') return 'H'
            return ''
          }),
          formatHours(resource.workedHours),
          resource.artifactCount,
        ]),
      ]
      fileName = `timegrid-timesheets-${monthSlug}.csv`
    } else if (activeView === 'resources') {
      const sortedMonths = [...resourceMonths].sort()
      const rangeSlug = sortedMonths.length === 1
        ? sortedMonths[0].slice(0, 7)
        : `${sortedMonths[0].slice(0, 7)}-to-${sortedMonths.at(-1).slice(0, 7)}`
      rows = [
        [
          'Month',
          'Resource',
          'Role',
          'Contact',
          'Vendor',
          'Start date',
          'Monthly hours',
          'Weekly files',
          'Resume',
          'Comments',
        ],
        ...resourceManagementRows.map((resource) => [
          resource.reportingMonthLabel,
          resource.name,
          resource.role || '',
          resource.email || '',
          resource.vendor_name || '',
          resource.start_date || '',
          formatHours(resource.workedHours),
          resource.artifactCount,
          resource.resume_label || '',
          resource.comments || '',
        ]),
      ]
      fileName = `timegrid-resources-${rangeSlug}.csv`
    } else if (activeView === 'evidence') {
      rows = [
        ['Month', 'Resource', 'Role', 'Contact', 'File', 'Uploaded', 'Assigned months'],
        ...filteredResourceStats.flatMap((resource) => {
          if (!resource.artifactItems.length) {
            return [[monthLabel, resource.name, resource.role || '', resource.email || '', '', '', '']]
          }

          return resource.artifactItems.map((artifact) => [
            monthLabel,
            resource.name,
            resource.role || '',
            resource.email || '',
            artifact.label,
            artifact.created_at ? formatArtifactDate(artifact.created_at) : '',
            artifactMonthValues(artifact).map(formatMonthStartLabel).join(' | '),
          ])
        }),
      ]
      exportedCount = rows.length - 1
      fileName = `timegrid-weekly-files-${monthSlug}.csv`
    } else {
      rows = [
        ['Month', 'Resource', 'Role', 'Contact', 'Monthly hours', 'Leave', 'Sick', 'Holiday', 'Weekly files', 'Status'],
        ...filteredResourceStats.map((resource) => {
          const isComplete = resource.workedHours > 0 && resource.artifactCount > 0
          return [
            monthLabel,
            resource.name,
            resource.role || '',
            resource.email || '',
            formatHours(resource.workedHours),
            resource.leaveDays,
            resource.sickDays,
            resource.holidayDays,
            resource.artifactCount,
            isComplete ? 'Complete' : resource.artifactCount ? 'Hours pending' : 'Files missing',
          ]
        }),
      ]
      fileName = `timegrid-management-report-${monthSlug}.csv`
    }

    downloadCsv(rows, fileName)
    showToast(`${exportedCount} row${exportedCount === 1 ? '' : 's'} exported.`)
  }

  if (!authReady) return <FullScreenLoading label="Checking your session…" />
  if (!demoMode && !session) return <Login onSignIn={signInWithPassword} />
  if (!demoMode && membershipLoading) return <FullScreenLoading label="Opening TimeGrid…" />
  if (!demoMode && setupError) {
    return (
      <SetupError
        error={setupError}
        onRetry={() => setMembershipAttempt((value) => value + 1)}
        onSignOut={signOut}
      />
    )
  }
  if (!demoMode && !membership) {
    return <AccessDenied email={session.user.email} onSignOut={signOut} />
  }

  const selectedResourceArtifacts = selectedArtifactsResource
    ? artifacts.filter((artifact) => artifact.resource_id === selectedArtifactsResource.id)
    : []
  const userLabel = demoMode
    ? 'Demo browser'
    : membership?.display_name || session?.user?.email?.split('@')[0] || 'Team member'
  const userEmail = demoMode ? 'Local preview mode' : session?.user?.email
  const viewMeta = VIEW_META[activeView] || VIEW_META.timesheets
  const isFiltered = Boolean(search.trim())
  const exportRowCount = activeView === 'resources' ? resourceManagementRows.length : filteredResourceStats.length

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark"><CalendarIcon size={22} /></span>
          <span>
            <strong>TimeGrid</strong>
            <small>Resource tracking</small>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          <span className="nav-label">Workspace</span>
          <button
            className={`nav-item ${activeView === 'timesheets' ? 'is-active' : ''}`}
            onClick={() => setActiveView('timesheets')}
            title="Timesheets"
            type="button"
          >
            <ClockIcon size={18} />
            Timesheets
          </button>
          <button
            className={`nav-item ${activeView === 'resources' ? 'is-active' : ''}`}
            onClick={() => setActiveView('resources')}
            title="Resource management"
            type="button"
          >
            <UsersIcon size={18} />
            Resource management
          </button>
          <button
            className={`nav-item ${activeView === 'evidence' ? 'is-active' : ''}`}
            onClick={() => setActiveView('evidence')}
            title="Evidence"
            type="button"
          >
            <FileIcon size={18} />
            Evidence
          </button>

          <span className="nav-label nav-label--spaced">Reporting</span>
          <button
            className={`nav-item ${activeView === 'reports' ? 'is-active' : ''}`}
            onClick={() => setActiveView('reports')}
            title="Management report"
            type="button"
          >
            <ChartIcon size={18} />
            Management report
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span className="sidebar-avatar">{initials(userLabel)}</span>
            <span>
              <strong>{userLabel}</strong>
              <small>{userEmail}</small>
            </span>
          </div>
          {demoMode ? (
            <button className="sidebar-action" onClick={resetDemo} title="Reset demo data" type="button">
              <RefreshIcon size={17} />
            </button>
          ) : (
            <>
              <button
                className="sidebar-action"
                onClick={() => setPasswordModalOpen(true)}
                title="Set or change password"
                type="button"
              >
                <LockIcon size={17} />
              </button>
              <button className="sidebar-action" onClick={signOut} title="Sign out" type="button">
                <LogOutIcon size={17} />
              </button>
            </>
          )}
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div className="mobile-brand">
            <span className="brand-mark"><CalendarIcon size={20} /></span>
            <strong>TimeGrid</strong>
          </div>

          <div className="topbar-title">
            <span className="eyebrow">{viewMeta.eyebrow}</span>
            <div className="month-control">
              <button
                aria-label="Previous month"
                className="icon-button"
                onClick={() => setMonth((current) => shiftMonth(current, -1))}
                type="button"
              >
                <ChevronLeftIcon />
              </button>
              <h1>{monthLabel}</h1>
              <button
                aria-label="Next month"
                className="icon-button"
                onClick={() => setMonth((current) => shiftMonth(current, 1))}
                type="button"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>

          <div className="topbar-actions">
            <label className="search-box">
              <SearchIcon size={17} />
              <input
                aria-label="Filter by resource name"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter resource name"
                type="search"
                value={search}
              />
            </label>
            <button
              className="button button--secondary topbar-export"
              disabled={!exportRowCount}
              onClick={exportCurrentViewCsv}
              title={`Export the visible ${viewMeta.label.toLowerCase()} rows to CSV`}
              type="button"
            >
              <DownloadIcon size={17} />
              <span className="button-label">Export CSV</span>
            </button>
            <button
              className="button button--secondary topbar-today"
              disabled={isCurrentMonth(month)}
              onClick={() => setMonth(startOfMonth(new Date()))}
              type="button"
            >
              Today
            </button>
            <button className="button button--primary" onClick={openAddResource} type="button">
              <PlusIcon size={17} />
              Add resource
            </button>
            {demoMode ? (
              <button
                aria-label="Reset demo data"
                className="icon-button mobile-account-action"
                onClick={resetDemo}
                title="Reset demo data"
                type="button"
              >
                <RefreshIcon size={17} />
              </button>
            ) : (
              <>
                <button
                  aria-label="Set or change password"
                  className="icon-button mobile-account-action"
                  onClick={() => setPasswordModalOpen(true)}
                  title="Set or change password"
                  type="button"
                >
                  <LockIcon size={17} />
                </button>
                <button
                  aria-label="Sign out"
                  className="icon-button mobile-account-action"
                  onClick={signOut}
                  title={`Sign out ${session?.user?.email || ''}`}
                  type="button"
                >
                  <LogOutIcon size={17} />
                </button>
              </>
            )}
          </div>
        </header>

        <nav className="mobile-view-nav" aria-label="Section navigation">
          <button
            className={activeView === 'timesheets' ? 'is-active' : ''}
            onClick={() => setActiveView('timesheets')}
            type="button"
          >
            <ClockIcon size={16} />
            Timesheets
          </button>
          <button
            className={activeView === 'resources' ? 'is-active' : ''}
            onClick={() => setActiveView('resources')}
            type="button"
          >
            <UsersIcon size={16} />
            Resources
          </button>
          <button
            className={activeView === 'evidence' ? 'is-active' : ''}
            onClick={() => setActiveView('evidence')}
            type="button"
          >
            <FileIcon size={16} />
            Evidence
          </button>
          <button
            className={activeView === 'reports' ? 'is-active' : ''}
            onClick={() => setActiveView('reports')}
            type="button"
          >
            <ChartIcon size={16} />
            Report
          </button>
        </nav>

        <div className="content-wrap">
          {demoMode ? (
            <div className="demo-banner">
              <InfoIcon size={18} />
              <span>
                <strong>Demo mode:</strong> this preview uses browser storage. Add Supabase environment variables to enable shared data, login and real file uploads.
              </span>
              <button onClick={resetDemo} type="button">Reset sample</button>
            </div>
          ) : null}

          {dataError ? (
            <div className="data-error data-error--panel">
              <span>{dataError}</span>
              <button className="button button--secondary" onClick={() => setReloadToken((value) => value + 1)} type="button">
                <RefreshIcon size={16} />
                Retry
              </button>
            </div>
          ) : null}

          {!dataError && activeView === 'timesheets' ? (
            <>
              <section className="timesheet-panel">
                <div className="panel-toolbar">
                  <div>
                    <h2>Resource calendar</h2>
                    <p>Click a day to enter worked hours or mark leave, sick leave or a holiday.</p>
                  </div>
                  <div className="legend" aria-label="Status legend">
                    <span><i className="legend-swatch legend-swatch--worked" />Worked</span>
                    <span><i className="legend-swatch legend-swatch--leave" />L Leave</span>
                    <span><i className="legend-swatch legend-swatch--sick" />SL Sick</span>
                    <span><i className="legend-swatch legend-swatch--holiday" />H Holiday</span>
                  </div>
                </div>

                {summary.missingEvidence > 0 && resources.length > 0 ? (
                  <div className="notice-strip">
                    <InfoIcon size={17} />
                    <span>
                      {summary.missingEvidence} resource{summary.missingEvidence === 1 ? '' : 's'} still {summary.missingEvidence === 1 ? 'has' : 'have'} no weekly file uploaded for {monthLabel}.
                    </span>
                  </div>
                ) : null}

                <div className="table-wrap">
                  {loading ? <div className="table-loading"><span className="loading-spinner" />Loading month…</div> : null}
                  <TimesheetGrid
                    artifacts={artifacts}
                    days={days}
                    isFiltered={isFiltered}
                    entryMap={entryMap}
                    onAddResource={openAddResource}
                    onArtifactsClick={setSelectedArtifactsResource}
                    onCellClick={(resource, day, entry) => setSelectedEntry({ resource, day, entry })}
                    onClearSearch={() => setSearch('')}
                    resources={filteredResources}
                  />
                </div>
              </section>
            </>
          ) : null}

          {!dataError && activeView === 'resources' ? (
            resourceMonthsLoading ? (
              <section className="section-panel section-panel--loading">
                <span className="loading-spinner" />
                Loading selected months…
              </section>
            ) : resourceMonthsError ? (
              <section className="section-panel resource-month-error">
                <div className="data-error data-error--panel">
                  <span>{resourceMonthsError}</span>
                  <button
                    className="button button--secondary"
                    onClick={() => setResourceMonthsReloadToken((value) => value + 1)}
                    type="button"
                  >
                    <RefreshIcon size={16} />
                    Retry
                  </button>
                </div>
              </section>
            ) : (
              <ResourceManagementView
                filtered={isFiltered}
                onAddResource={openAddResource}
                onChooseMonths={() => setMonthSelectorOpen(true)}
                onClearSearch={() => setSearch('')}
                onEditResource={openEditResource}
                onOpenEvidence={openResourceEvidence}
                onOpenResume={openResourceResume}
                onOpenTimesheet={openResourceTimesheet}
                rows={resourceManagementRows}
                selectedMonths={resourceMonths}
              />
            )
          ) : null}

          {!dataError && activeView === 'evidence' ? (
            loading ? (
              <section className="section-panel section-panel--loading">
                <span className="loading-spinner" />
                Loading evidence…
              </section>
            ) : (
              <EvidenceView
                filtered={isFiltered}
                monthLabel={monthLabel}
                onAddResource={openAddResource}
                onClearSearch={() => setSearch('')}
                onManageEvidence={setSelectedArtifactsResource}
                resources={filteredResourceStats}
              />
            )
          ) : null}

          {!dataError && activeView === 'reports' ? (
            loading ? (
              <section className="section-panel section-panel--loading">
                <span className="loading-spinner" />
                Preparing report…
              </section>
            ) : (
              <ManagementReportView
                filtered={isFiltered}
                monthLabel={monthLabel}
                onAddResource={openAddResource}
                onClearSearch={() => setSearch('')}
                resources={filteredResourceStats}
              />
            )
          ) : null}
        </div>
      </main>

      {selectedEntry ? (
        <EntryModal
          day={selectedEntry.day}
          entry={selectedEntry.entry}
          onClose={() => setSelectedEntry(null)}
          onSave={saveEntry}
          resource={selectedEntry.resource}
          saving={saving}
        />
      ) : null}

      {monthSelectorOpen ? (
        <MonthSelectorModal
          currentMonth={monthStart}
          onApply={(months) => {
            setResourceMonths(months)
            setMonthSelectorOpen(false)
          }}
          onClose={() => setMonthSelectorOpen(false)}
          selectedMonths={resourceMonths}
        />
      ) : null}

      {resourceModalOpen ? (
        <ResourceModal
          onClose={closeResourceModal}
          onOpenResume={openResourceResume}
          onSave={saveResource}
          resource={editingResource}
          saving={saving}
        />
      ) : null}

      {selectedArtifactsResource ? (
        <ArtifactModal
          artifacts={selectedResourceArtifacts}
          demoMode={demoMode}
          monthLabel={monthLabel}
          monthStart={monthStart}
          onClose={() => setSelectedArtifactsResource(null)}
          onDelete={deleteArtifact}
          onOpen={openArtifact}
          onUpload={uploadArtifacts}
          resource={selectedArtifactsResource}
        />
      ) : null}

      {passwordModalOpen && !demoMode ? (
        <PasswordModal
          onClose={() => setPasswordModalOpen(false)}
          onSave={updatePassword}
          saving={saving}
        />
      ) : null}

      {toast ? (
        <div className={`toast toast--${toast.type}`} role="status">
          {toast.message}
        </div>
      ) : null}
    </div>
  )
}
